import { QuestionAnswerMap } from '@care-portals/api-interfaces';
import {
  GenericHeightWeightQuestionFieldAnswers,
  GenericTerminalQuestionFieldAnswers
} from '../../Generic/questions';

const weightLossCvQuestions = [
  {
    field: 'weightLossCv_intro',
    label:
      'This consultation is designed to evaluate your cardiovascular and metabolic risk. In addition to lifestyle measures—such as nutrition, physical activity, sleep, and stress management—your clinician may discuss medical treatment options that could help reduce your risk when appropriate. This can take 5-10 minutes to complete. Before beginning your intake form, please gather the information needed to make your consultation as smooth and safe as possible.',
    subLabel:
      'If you are already taking weight-loss or cardiovascular medications, have your prescription details available so we can confirm the correct medication and dose. If it is unclear, we will ask you to upload a photo of your prescription so we can confirm the dose for you. Please make sure that everything is accurate on your intake form. After your information is sent to the doctor, you will receive a greeting message—please check your messages regularly so your care team can stay in touch.',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Lets get started', key: 'letsGetStarted' },
      ]
    },
    extras: { sendToBeluga: false },
  },
  {
    field: 'weightLossCv_bloodPressure',
    label:
      'Blood pressure is a major risk factor for cardiovascular disease. What was your most recent blood pressure reading? Please select the closest range. If your top and bottom numbers fall into different ranges, choose the higher range.',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Low: Less than 90/60', key: 'low' },
        { value: 'Normal or mildly elevated: Less than 130/80', key: 'normal' },
        {
          value:
            'High: 130–159 (top number) or 80–99 (bottom number)',
          key: 'high'
        },
        {
          value:
            'Very high: 160–179 (top number) or 100–119 (bottom number)',
          key: 'veryHigh'
        },
        {
          value:
            'Severely high: 180 or higher (top number) or 120 or higher (bottom number)',
          key: 'severelyHigh'
        },
        { value: 'I don’t know my blood pressure', key: 'unknown' }
      ]
    }
  },
  {
    field: 'weightLossCv_restingHeartRate',
    label:
      'What was your most recent resting heart rate? Please select the closest range. Measure your pulse while seated, calm, and at rest.',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Low: Less than 50 beats per minute', key: 'low' },
        { value: 'Mildly low: 50–59 beats per minute', key: 'mildlyLow' },
        { value: 'Normal: 60–100 beats per minute', key: 'normal' },
        { value: 'High: 101 beats per minute or more', key: 'high' }
      ]
    }
  },
  {
    field: 'weightLossCv_cvdRiskFactors',
    label:
      'Have you been diagnosed with any of the following risk factors for cardiovascular disease?',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'High blood pressure', key: 'highBloodPressure' },
        {
          value: 'High cholesterol or high triglycerides',
          key: 'highCholesterol'
        },
        {
          value:
            'Familial hypercholesterolemia or another inherited cholesterol disorder',
          key: 'familialHypercholesterolemia'
        },
        { value: 'Prediabetes or diabetes', key: 'prediabetesOrDiabetes' },
        {
          value: 'Chronic Kidney Disease Stage 3b or greater',
          key: 'ckd3b'
        },
        {
          value: 'Overweight, obesity, or metabolic syndrome',
          key: 'overweightObesityMetabolic'
        },
        { value: 'Obstructive sleep apnea', key: 'sleepApnea' },
        {
          value:
            'Rheumatoid arthritis, lupus, psoriasis, inflammatory bowel disease, HIV, or another chronic inflammatory condition',
          key: 'chronicInflammatory'
        },
        { value: 'None of the above', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'weightLossCv_familyEarlyCvd',
    label:
      'Has a parent or sibling experienced a heart attack, stroke, coronary stent, bypass surgery, or other cardiovascular disease at an early age?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Male relative before age 55', key: 'maleRelativeBefore55' },
        {
          value: 'Female relative before age 65',
          key: 'femaleRelativeBefore65'
        },
        { value: 'Yes, but I do not know their age', key: 'yesUnknownAge' },
        { value: 'No', key: 'no' },
        { value: 'Unsure', key: 'unsure' }
      ]
    }
  },
  {
    field: 'weightLossCv_suddenDeathFamily',
    label:
      'Has a parent, sibling, or child experienced an unexplained sudden death or cardiac arrest before age 50?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes', key: 'yes' },
        { value: 'Unsure', key: 'unsure' }
      ]
    }
  },
  {
    field: 'weightLossCv_tobaccoUse',
    label:
      'Which best describes your use of cigarettes, cigars, smokeless tobacco, or other tobacco products?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Never used', key: 'never' },
        { value: 'Former use', key: 'former' },
        { value: 'Current occasional use', key: 'occasional' },
        { value: 'Current daily use', key: 'daily' }
      ]
    }
  },
  {
    field: 'weightLossCv_activityDays',
    label:
      'On average, how many days per week do you participate in physical activity?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I do not regularly participate in physical activity',
          key: 'none'
        },
        { value: '1 day per week', key: 'days1' },
        { value: '2 days per week', key: 'days2' },
        { value: '3 days per week', key: 'days3' },
        { value: '4 days per week', key: 'days4' },
        { value: '5 days per week', key: 'days5' },
        { value: '6 days per week', key: 'days6' },
        { value: '7 days per week', key: 'days7' }
      ]
    }
  },
  {
    field: 'weightLossCv_activityDuration',
    label:
      'On the days you exercise, how long are you typically physically active?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Less than 15 minutes', key: 'under15' },
        { value: '15–29 minutes', key: 'min15to29' },
        { value: '30–44 minutes', key: 'min30to44' },
        { value: '45–59 minutes', key: 'min45to59' },
        { value: '60–89 minutes', key: 'min60to89' },
        { value: '90 minutes or longer', key: 'min90Plus' },
        {
          value: 'Not applicable—I do not regularly exercise',
          key: 'notApplicable'
        }
      ]
    }
  },
  {
    field: 'weightLossCv_activityIntensity',
    label: 'How would you describe the usual intensity of your physical activity?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Light—minimal increase in breathing or heart rate',
          key: 'light'
        },
        {
          value:
            'Moderate—noticeable increase in breathing and heart rate, but able to speak',
          key: 'moderate'
        },
        {
          value:
            'Vigorous—significant increase in breathing and heart rate, with difficulty speaking in full sentences',
          key: 'vigorous'
        },
        {
          value: 'A combination of moderate and vigorous activity',
          key: 'combination'
        },
        {
          value: 'Not applicable—I do not regularly exercise',
          key: 'notApplicable'
        }
      ]
    }
  },
  {
    field: 'weightLossCv_processedFoods',
    label:
      'How often do you consume highly processed foods or sugary drinks, such as packaged snacks, frozen meals, fast food, soda, sweet tea, energy drinks, or sweetened coffee beverages?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Rarely or never', key: 'rarely' },
        { value: 'Less than once per week', key: 'lessThanWeekly' },
        { value: '1–2 times per week', key: 'times1to2' },
        { value: '3–5 times per week', key: 'times3to5' },
        { value: 'Daily or almost daily', key: 'daily' },
        { value: 'More than once daily', key: 'moreThanOnceDaily' }
      ]
    }
  },
  {
    field: 'weightLossCv_alcohol',
    label: 'On average, how many alcoholic drinks do you consume per week?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'I do not drink alcohol', key: 'none' },
        { value: 'Less than 1 drink per week', key: 'lessThan1' },
        { value: '1–3 drinks per week', key: 'drinks1to3' },
        { value: '4–7 drinks per week', key: 'drinks4to7' },
        { value: '8–14 drinks per week', key: 'drinks8to14' },
        { value: '15–20 drinks per week', key: 'drinks15to20' },
        { value: 'More than 20 drinks per week', key: 'moreThan20' }
      ]
    }
  },
  {
    field: 'weightLossCv_medicalConditions',
    label: 'Do any of these apply to you?:',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Liver failure/liver cirrhosis', key: 'liverFailure' },
        {
          value: 'Persistent inflammation of liver or elevated liver enzymes',
          key: 'liverInflammation'
        },
        { value: 'History of rhabdomyolysis', key: 'rhabdomyolysis' },
        {
          value: 'A muscular or neuromuscular disorder',
          key: 'neuromuscular'
        },
        {
          value: 'Gastroparesis (Paralysis of your intestines)',
          key: 'gastroparesis'
        },
        { value: 'Triglycerides over 600 at any point', key: 'triglyceridesOver600' },
        {
          value: 'Pancreatic cancer or pancreatitis',
          key: 'pancreaticCancerOrPancreatitis'
        },
        {
          value: 'Prediabetes or type 2 diabetes without insulin use',
          key: 'prediabetesType2NoInsulin'
        },
        {
          value: 'Type 1 Diabetes/Insulin-dependent diabetes',
          key: 'type1Diabetes'
        },
        {
          value: 'Moderate to severe diabetic retinopathy',
          key: 'diabeticRetinopathy'
        },
        { value: 'Hypoglycemia (low blood sugar)', key: 'hypoglycemia' },
        {
          value: 'Personal or family history of medullary thyroid cancer',
          key: 'mtc'
        },
        {
          value:
            'Personal or family history of Multiple Endocrine Neoplasia (MEN-2) syndrome',
          key: 'men2'
        },
        { value: 'Anorexia or bulimia', key: 'anorexiaBulimia' },
        {
          value:
            'Syndrome of Inappropriate Antidiuretic Hormone (SIADH)',
          key: 'siadh'
        },
        {
          value: 'Current symptomatic gallstones',
          key: 'symptomaticGallstones'
        },
        {
          value: 'Gastric bypass in the past 6 months',
          key: 'gastricBypass6Months'
        },
        {
          value: 'Current gallstones without symptoms',
          key: 'asymptomaticGallstones'
        },
        {
          value: 'Past removal of your gallbladder',
          key: 'cholecystectomy'
        },
        {
          value: 'Hypothyroidism, Hyperthyroidism, or Thyroid Issues',
          key: 'thyroidIssues'
        },
        { value: 'None of the above', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'weightLossCv_gallstonesConsent',
    label: 'Consent (Gallstones):',
    type: 'select',
    educationalSlide:
      'Use of GLP-1 medications, including semaglutide, tirzepatide, and similar products, has been associated with an increased risk of gallbladder or biliary issues, including the development or worsening of gallstones. If you have existing but asymptomatic gallstones, treatment may increase the chance that these stones become symptomatic, potentially leading to pain, inflammation, or the need for medical or surgical treatment. Notify your provider if you develop new upper abdominal pain, nausea, or vomiting during treatment. Knowing this risk, do you wish to continue?',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I do NOT wish to receive a GLP product',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read and understand the information above and wish to proceed',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'weightLossCv_cholecystectomyConsent',
    label: 'Consent (Cholecystectomy):',
    type: 'select',
    educationalSlide:
      'You reported a history of gallbladder removal. This medication may still be appropriate but can alter fat and bile digestion, increasing the risk of side effects such as abdominal pain or diarrhea. It may also affect absorption of fat-soluble vitamins (A, D, E, and K); smaller meals, avoiding processed foods, and a daily multivitamin may help. Seek immediate care for severe abdominal pain, fever, or vomiting.',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I do NOT wish to receive a GLP product',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read and understand the information above and wish to proceed',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'weightLossCv_medicalConditionsDetail',
    label: 'Please tell us more about your medical condition(s) that you selected',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'weightLossCv_medicalConditionsDetail'
    }
  },
  {
    field: 'weightLossCv_glpAllergy',
    label:
      'Are you allergic to any GLP-1 medications such as Ozempic/Wegovy (Semaglutide), Mounjaro/Zepbound (Tirzepatide), Saxenda (Liraglutide), or Trulicity (Dulaglutide)?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'weightLossCv_statinUse',
    label:
      'Which best describes your previous use of statin medication such as atorvastatin (Lipitor), rosuvastatin (Crestor), simvastatin (Zocor), etc.?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'I have never taken a statin', key: 'neverTaken' },
        {
          value: 'I have taken a statin without significant side effects',
          key: 'noSignificantSideEffects'
        },
        {
          value:
            'I stopped one statin because of muscle pain, cramping, or weakness',
          key: 'stoppedOneMuscle'
        },
        {
          value:
            'I stopped more than one statin because of muscle pain, cramping, or weakness',
          key: 'stoppedMultipleMuscle'
        },
        {
          value:
            'I stopped a statin because of elevated liver enzymes or liver injury',
          key: 'stoppedLiver'
        },
        {
          value: 'I stopped a statin for a reaction not listed above',
          key: 'stoppedOther'
        }
      ]
    }
  },
  {
    field: 'weightLossCv_statinDetails',
    label:
      'Please provide further information about what you experienced and which medication you have taken',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'weightLossCv_statinDetails'
    }
  },
  {
    field: 'weightLossCv_hypoglycemiaMeds',
    label:
      'Do you take any of the following medications?\n\nInsulin\nGlimepiride (Amaryl)\nMeglitinides such as repaglinide or nateglinide\nGlipizide (Glucotrol and Glucotrol XL)\nGlyburide (Micronase, Glynase, and Diabeta)\nSitagliptin\nSaxagliptin\nLinagliptin\nAlogliptin',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'weightLossCv_interactingMeds',
    label: 'Are you taking any of the following medications?? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value:
            'Certain antibiotics, antifungals,or HIV medications (examples: ketoconazole, itraconazole, and/or ritonavir)',
          key: 'cypInhibitors'
        },
        {
          value:
            'Seizure medications (examples: carbamazepine, phenytoin, phenobarbital) or St. John’s Wort',
          key: 'seizureMeds'
        },
        {
          value: 'Simvastatin/Zocor with a dose of 30mg or higher',
          key: 'simvastatinHighDose'
        },
        {
          value: 'Birth control pills or other oral hormonal contraceptives',
          key: 'oralContraceptives'
        },
        { value: 'None of the above', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'weightLossCv_recentWeightLossMeds',
    label:
      'Have you taken any of the following weight loss medications within the past 4 weeks?',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'Semaglutide (Ozempic, Wegovy, Rybelsus)',
          key: 'semaglutide'
        },
        {
          value: 'Tirzepatide (Zepbound, Mounjaro)',
          key: 'tirzepatide'
        },
        { value: 'None of these', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'weightLossCv_lastTookMedication',
    label: 'When did you last take your prescribed weight loss medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Within the past week', key: 'pastWeek' },
        { value: 'Within the past 2-4 weeks', key: 'past2to4Weeks' }
      ]
    }
  },
  {
    field: 'weightLossCv_restartLowerDose',
    label:
      'Because these medications build up gradually in your system, restarting at a high dose after a break can increase the risk of side effects. For safety, we recommend continuing at a lower dose (usually about 50% of your previous dose) and gradually increasing if appropriate. How would you like to proceed?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I understand and agree to continue at a lower dose',
          key: 'agreeLowerDose'
        },
        {
          value: 'I would like to discuss this further with the provider',
          key: 'discussWithProvider'
        }
      ]
    }
  },
  {
    field: 'weightLossCv_lastInjectionDate',
    label:
      'What was the approximate date you last injected your GLP-1 medication?',
    type: 'info',
    answer: {
      type: 'text',
      name: 'weightLossCv_lastInjectionDate',
      placeholder: 'Approximate date'
    }
  },
  {
    field: 'weightLossCv_currentDose',
    label:
      'Accurate dosing is important for your safety. Select the exact medication and most recent milligram (mg) dose you administered. This may not be accurately displayed on your medication label and could be within your prescribers message to you. Do not estimate. The vial concentration (displayed mg/ml) or the volume you inject (units or ml) you inject is not the same as your prescribed dose (which is reflected as mg).\n\nIf you are unsure, select “I don’t know.” You will be asked to enter the prescription details or upload a clear photo so your care team can verify this for you.\n\nBy selecting a dose, you confirm that it accurately reflects your medication and most recent administered dose. You may provide additional information at the end of the intake form or message your provider directly to provide further information. (Select one)',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Semaglutide/Ozempic/Wegovy 0.25mg',
          key: 'semaglutide_0_25'
        },
        { value: 'Semaglutide/Ozempic/Wegovy 0.5mg', key: 'semaglutide_0_5' },
        { value: 'Semaglutide/Ozempic/Wegovy 1mg', key: 'semaglutide_1' },
        {
          value: 'Semaglutide/Ozempic/Wegovy 1.5 or 1.7mg',
          key: 'semaglutide_1_5_or_1_7'
        },
        { value: 'Semaglutide/Ozempic/Wegovy 2.0mg', key: 'semaglutide_2' },
        { value: 'Semaglutide/Ozempic/Wegovy 2.4mg', key: 'semaglutide_2_4' },
        {
          value: 'Tirzepatide/Mounjaro/Zepbound 2.5mg',
          key: 'tirzepatide_2_5'
        },
        { value: 'Tirzepatide/Mounjaro/Zepbound 5mg', key: 'tirzepatide_5' },
        {
          value: 'Tirzepatide/Mounjaro/Zepbound 7.5mg',
          key: 'tirzepatide_7_5'
        },
        { value: 'Tirzepatide/Mounjaro/Zepbound 10mg', key: 'tirzepatide_10' },
        {
          value: 'Tirzepatide/Mounjaro/Zepbound 12.5mg',
          key: 'tirzepatide_12_5'
        },
        { value: 'Tirzepatide/Mounjaro/Zepbound 15mg', key: 'tirzepatide_15' },
        { value: 'I don’t see my dose', key: 'dontSeeDose' },
        { value: 'I don’t know my dose', key: 'dontKnowDose' }
      ]
    }
  },
  {
    field: 'weightLossCv_unlistedDose',
    label: 'Please provide us with your current dose',
    type: 'info',
    answer: {
      type: 'text',
      name: 'weightLossCv_unlistedDose'
    }
  },
  {
    field: 'weightLossCv_brandOrCompounded',
    label:
      'Are you currently using a brand name medication (like Wegovy or Zepbound) or a compounded version (like semaglutide or tirzepatide)?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'I’m taking a brand name medication', key: 'brandName' },
        { value: 'I’m taking a compounded product', key: 'compounded' }
      ]
    }
  },
  {
    field: 'weightLossCv_shareDoseMethod',
    label:
      'Here’s how to share the information clearly to help your provider determine your dose. How would you like to proceed?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I would like to type out the information on the label',
          key: 'typeOut'
        },
        {
          value: 'I would like to take a picture of my medication',
          key: 'picture'
        }
      ]
    }
  },
  {
    field: 'weightLossCv_labelText',
    label:
      'Please provide the information from your label here:\n1. Medication name – write the exact name from your prescription label including any additives such as glycine, vitamin B, etc.\n2. Concentration – look on your prescription label or vial for something that looks like “20 mg/mL” or “2.5 mg/mL.” There may be several numbers which should all be provided. This shows how strong your medication is. Please provide all the numbers listed.\n3. Amount you took – write how much you injected or used, in units or milliliters (mL).\n(Example: “Medication: Semaglutide. Concentration: 2.5 mg/mL. Dose taken: 0.5 mL.”)',
    type: 'info',
    answer: {
      type: 'text',
      rows: 6,
      name: 'weightLossCv_labelText'
    }
  },
  {
    field: 'weightLossCv_labelPhoto',
    label:
      'If you’re unsure about your prescription details, please upload a clear photo of your medication label. Make sure the entire label is visible, and let us know how many units or milliliters (mL) you are currently using.',
    type: 'file_upload',
    accepts: ['application/pdf', 'image/png', 'image/jpeg'],
    answer: [
      {
        resourceId: 'weightLossCv_labelPhoto',
        buttonLabel: 'Upload photo'
      }
    ]
  },
  {
    field: 'weightLossCv_sideEffects',
    label: 'Have you experienced side effects from your current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'weightLossCv_sideEffectsDetail',
    label: "Please describe the side effects that you've experienced",
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'weightLossCv_sideEffectsDetail'
    }
  },
  {
    field: 'weightLossCv_continueTreatment',
    label: 'How would you like to continue your treatment?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'Stay at the same dose or equivalent dose',
          key: 'sameDose'
        },
        {
          value:
            "Increase the dose if a higher one is available, or continue with my current dose if it's already at the maximum",
          key: 'increaseDose'
        },
        { value: 'Decrease dose', key: 'decreaseDose' }
      ]
    }
  },
  {
    field: 'weightLossCv_sexAssignedAtBirth',
    label: 'What was your sex assigned at birth?',
    extras: { sendToBeluga: false },
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Male', key: 'male' },
        { value: 'Female', key: 'female' }
      ]
    }
  },
  {
    field: 'weightLossCv_pregnancy',
    label:
      'Are you currently pregnant, breastfeeding or planning to become pregnant?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        {
          value:
            'No and I acknowledge that these medications are not safe to use during pregnancy or while breastfeeding, as they may pose risks to a developing baby.',
          key: 'no'
        }
      ]
    }
  },
  {
    field: 'weightLossCv_otherInfo',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'weightLossCv_otherInfo'
    }
  },
  {
    field: 'weightLossCv_treatmentConsent',
    label: 'Consent (Cardiovascular and Metabolic Risk-Reduction Treatment):',
    type: 'select',
    educationalSlide: `You are requesting a medical evaluation to identify and reduce your risk of cardiovascular disease, including heart attack and stroke, and to improve your overall metabolic health.

Your clinician will review the information provided in your intake form, including your medical and family history, current medications, lifestyle, weight, blood pressure, laboratory results, and cardiovascular risk factors. Based on this assessment, your clinician will determine the most appropriate intervention for you.

Your treatment plan may include:
- Recommendations related to nutrition, physical activity, sleep, stress management, tobacco use, and other lifestyle factors
- A weight-management medication
- A cholesterol-lowering medication, such as a statin
- A combination of weight-management medication and statin therapy
- Additional laboratory testing or medical evaluation
- Referral to a primary care clinician, cardiologist, or another healthcare professional
- No medication when treatment is not indicated or cannot be safely prescribed

You are not guaranteed a prescription for any particular medication. Your clinician may determine that additional testing, an in-person examination, or treatment through another healthcare professional is needed before medication can be prescribed.

Weight-Management Medication
When clinically appropriate, your treatment may include a GLP-1 receptor agonist or a combined GIP/GLP-1 receptor agonist. Examples include semaglutide products, such as Wegovy or Ozempic, and tirzepatide products, such as Zepbound or Mounjaro.

These medications act on hormones involved in appetite, fullness, food intake, blood glucose regulation, and stomach emptying. They may support weight reduction, long-term weight management, and improvement in certain metabolic risk factors. Some medications in this class have an FDA-approved cardiovascular risk-reduction indication for specific patient populations, while others are approved primarily for weight management or diabetes.

Weight-management medications are generally prescribed for individuals with obesity or for individuals who are overweight and have an associated medical condition. A clinician may sometimes prescribe a medication for a use that is not specifically included in its FDA-approved labeling. This is known as off-label prescribing and may be considered when supported by clinical judgment.

Statin Therapy
Your treatment may include a statin medication, such as atorvastatin or rosuvastatin. Statins lower LDL cholesterol and may reduce the risk of heart attack, stroke, and other major cardiovascular events in appropriately selected patients.

The decision to prescribe a statin will be based on factors such as your cholesterol levels, age, blood pressure, diabetes status, tobacco use, family history, prior cardiovascular disease, kidney function, medication history, and overall cardiovascular risk.

Potential Benefits
Depending on the intervention selected, potential benefits may include:
- Weight loss or improved long-term weight management
- Lower LDL cholesterol
- Improved blood glucose control
- Improvement in certain cardiovascular and metabolic risk factors
- Reduced risk of heart attack, stroke, or other cardiovascular events in appropriately selected patients
- Improved overall metabolic health

Individual results vary. Treatment does not eliminate cardiovascular risk, and no specific amount of weight loss, cholesterol reduction, or reduction in cardiovascular events can be guaranteed.

Risks and Side Effects of Weight-Management Medications
Common side effects may include nausea, vomiting, diarrhea, constipation, abdominal discomfort or bloating, indigestion or acid reflux, decreased appetite, fatigue, dizziness, headache, injection-site reactions, and hair loss.

Serious risks may include pancreatitis, gallstones or gallbladder inflammation, severe or persistent gastrointestinal symptoms, delayed stomach emptying or worsening of gastroparesis, dehydration and acute kidney injury, low blood sugar particularly when combined with insulin or a sulfonylurea, serious allergic reactions including anaphylaxis or angioedema, worsening of diabetic retinopathy in some patients with diabetes, increased heart rate, and pulmonary aspiration during general anesthesia or deep sedation.

Although uncommon, serious complications may require emergency treatment or hospitalization and may be life-threatening.

Thyroid C-Cell Tumor Warning
Animal studies involving semaglutide and tirzepatide have shown an increased risk of thyroid C-cell tumors. It is not known whether these medications cause medullary thyroid carcinoma in humans. These medications should not be used if you or a family member has had medullary thyroid carcinoma or if you have Multiple Endocrine Neoplasia syndrome type 2. Contact your healthcare provider if you develop a neck mass, persistent hoarseness, difficulty swallowing, or difficulty breathing.

Risks and Side Effects of Statin Therapy
Common side effects may include muscle aches or soreness, headache, nausea, abdominal discomfort, weakness or fatigue, and constipation.

Serious risks may include severe muscle injury or rhabdomyolysis, kidney injury related to severe muscle breakdown, liver injury or significant elevation of liver enzymes, serious allergic reactions, and a small increase in blood glucose or the risk of developing diabetes in susceptible individuals.

The risk of muscle injury may be greater with certain medications, severe kidney disease, inadequately treated hypothyroidism, higher statin doses, or a history of statin-associated muscle problems. You must provide a complete list of prescription medications, over-the-counter medications, vitamins, and supplements so your clinician can evaluate potential interactions.

Contact your clinician promptly if you develop unexplained muscle pain, tenderness, or weakness. Seek urgent medical attention if these symptoms are severe or are accompanied by dark or cola-colored urine, significant weakness, fever, or reduced urination.

Contact your clinician if you develop yellowing of the skin or eyes, dark urine, unusual fatigue, loss of appetite, or persistent pain in the upper-right abdomen, as these may indicate liver injury.

Pregnancy, Contraception, and Breastfeeding
Weight-loss treatment is not recommended during pregnancy. Notify your clinician immediately if you are pregnant, believe you may be pregnant, or are planning pregnancy. If you are prescribed semaglutide, it should generally be discontinued when pregnancy is recognized and at least two months before a planned pregnancy.

Tirzepatide may reduce the effectiveness of oral hormonal contraceptives because it delays stomach emptying. If you use oral hormonal birth control, you should switch to a non-oral contraceptive method or add a barrier method, such as condoms, for four weeks after starting tirzepatide and for four weeks after each dose increase.

Most patients should stop statin therapy during pregnancy. Patients taking a statin should notify their clinician immediately if pregnancy occurs or is suspected. Breastfeeding is generally not recommended while taking a statin. Breastfeeding recommendations for semaglutide and tirzepatide depend on the medication and formulation. Discuss breastfeeding with your clinician before beginning or continuing treatment.

Additional Safety Considerations
You must tell your clinician if you have a personal or family history of medullary thyroid carcinoma or Multiple Endocrine Neoplasia syndrome type 2; have had pancreatitis, gallbladder disease, severe gastroparesis, or significant gastrointestinal disease; have kidney disease, liver disease, or unexplained abnormal liver tests; have diabetes or diabetic retinopathy; use insulin, a sulfonylurea, or another medication that may cause low blood sugar; have previously experienced severe muscle injury or liver injury from a statin; are taking another GLP-1, semaglutide-containing, tirzepatide-containing, or statin medication; consume large amounts of alcohol; are scheduled for surgery, general anesthesia, or deep sedation; or are pregnant, planning pregnancy, or breastfeeding.

Do not use more than one GLP-1 or GIP/GLP-1 medication unless specifically directed by your clinician. Tell the healthcare professional performing any surgery or procedure that you take a medication that may delay stomach emptying.

Monitoring and Follow-Up
Regular follow-up is necessary to assess your response to treatment, monitor for side effects, and determine whether treatment should be continued, adjusted, or discontinued. Monitoring may include weight and body measurements, blood pressure and heart rate, cholesterol testing, blood glucose or hemoglobin A1c, liver or kidney function testing, evaluation of muscle symptoms, review of medication adherence and side effects, and updated cardiovascular risk assessment.

Additional laboratory testing may be ordered when clinically indicated. You may also be asked to provide objective documentation of your weight or other measurements as part of treatment monitoring.

You agree to notify your clinician when there is a significant change in your health, medications, pregnancy status, or medical history. You understand that failure to complete recommended follow-up may result in medication being withheld or discontinued.

Alternatives to Treatment
Alternatives may include lifestyle intervention without medication, treatment through your primary care clinician or a specialist, another weight-management or cholesterol-lowering medication, additional diagnostic testing, or choosing not to begin treatment. You may decline a recommended medication or discontinue treatment after discussing safe discontinuation and alternative options with your clinician.

When to Seek Immediate Care
Seek emergency medical attention for chest pain, pressure, or severe shortness of breath; sudden weakness, numbness, facial drooping, difficulty speaking, or another possible stroke symptom; difficulty breathing or swelling of the face, lips, tongue, or throat; severe or persistent abdominal pain, particularly with vomiting; severe muscle pain or weakness with dark urine; yellowing of the skin or eyes; or severe dehydration, fainting, confusion, or inability to keep fluids down. This service is not a substitute for emergency medical care.

Patient Attestation and Consent
I verify that I am the patient completing this intake form. I confirm that I have reviewed and understood the questions asked of me and that the information I provided is true, accurate, and complete to the best of my knowledge.

I understand that complete and accurate information is necessary for my clinician to safely evaluate my cardiovascular and metabolic risk and select an appropriate treatment. I understand that inaccurate, incomplete, or omitted information may result in an unsafe or inappropriate treatment decision.

I understand that my clinician will determine the most appropriate intervention based on my medical information and clinical judgment. The recommended plan may include lifestyle treatment, a weight-management medication, a statin, both medication types, additional testing, referral, or no medication.

I acknowledge that treatment may involve an FDA-approved use or an off-label use when determined to be clinically appropriate.

I acknowledge the potential benefits, limitations, risks, side effects, alternatives, and monitoring requirements described above. I understand that no specific medical outcome is guaranteed.

I consent to this cardiovascular and metabolic risk-reduction evaluation and, when determined clinically appropriate and communicated to me, treatment with a weight-management medication, statin therapy, or a combination of these interventions.`,
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read and understand the above information and I wish to continue',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  }
] as const;

export type WeightLossCVQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof weightLossCvQuestions>
> &
  GenericHeightWeightQuestionFieldAnswers &
  GenericTerminalQuestionFieldAnswers;
export default weightLossCvQuestions;
